/**
 * AddColourCodeModal — add a per-org PixelWrangler colour-code rule (Phase 63).
 *
 * Two modes, chosen with a RadioGroup so the "rewrite to" field is never a bare
 * optional input (mirrors line-item-exclusions/AddRuleModal):
 *   - Rewrite   → the filename code differs from the order SKU (WHT → WH).
 *   - Recognise → the code already matches the order SKU; just stop skipping it (BL).
 *
 * Codes are uppercase-normalised + validated client-side against the same
 * 1–10 alphanumeric class as the DB CHECK + the backend Zod schema. A live
 * preview sentence (aria-live) states what the bridge will do.
 */
import { useMemo, useState } from 'react';
import { ArrowRight, Palette, Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

const CODE_RE = /^[A-Za-z0-9]{1,10}$/;

type Mode = 'rewrite' | 'recognise';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Uppercase pw codes already configured — for the duplicate guard. */
  existingCodes: string[];
  onAdd: (input: { pwCode: string; canonicalCode?: string; note?: string }) => void;
  isPending: boolean;
}

const MODE_OPTIONS: ReadonlyArray<{
  id: Mode;
  icon: typeof Pencil;
  title: string;
  desc: string;
}> = [
  {
    id: 'rewrite',
    icon: Pencil,
    title: 'Rewrite it to another code',
    desc: 'The PixelWrangler filename code differs from your order SKU, for example WHT becomes WH.',
  },
  {
    id: 'recognise',
    icon: Palette,
    title: 'Just recognise it',
    desc: 'The code already matches your order SKU; the bridge should stop skipping it, for example BL.',
  },
];

export function AddColourCodeModal({ open, onClose, existingCodes, onAdd, isPending }: Props) {
  const [mode, setMode] = useState<Mode>('rewrite');
  const [pwCode, setPwCode] = useState('');
  const [canonicalCode, setCanonicalCode] = useState('');
  const [note, setNote] = useState('');

  const reset = () => {
    setMode('rewrite');
    setPwCode('');
    setCanonicalCode('');
    setNote('');
  };
  const close = () => {
    reset();
    onClose();
  };

  const pw = pwCode.trim().toUpperCase();
  const canon = canonicalCode.trim().toUpperCase();
  const pwValid = CODE_RE.test(pw);
  const canonValid = CODE_RE.test(canon);
  const isDuplicate = pwValid && existingCodes.includes(pw);
  const isSelfRewrite = mode === 'rewrite' && pwValid && canonValid && pw === canon;

  let error: string | null = null;
  if (pwCode.trim() && !pwValid) {
    error = 'Codes must be 1 to 10 letters or digits (no spaces or symbols).';
  } else if (isDuplicate) {
    error = `"${pw}" already has a rule. Delete it first to change it.`;
  } else if (mode === 'rewrite' && canonicalCode.trim() && !canonValid) {
    error = 'The rewrite code must be 1 to 10 letters or digits.';
  } else if (isSelfRewrite) {
    error = 'A code cannot rewrite to itself. Choose "Just recognise it" instead.';
  }

  const canSubmit =
    pwValid &&
    !isDuplicate &&
    (mode === 'recognise' || (canonValid && !isSelfRewrite)) &&
    !isPending;

  const preview = useMemo(() => {
    if (!pwValid) return null;
    if (mode === 'recognise') {
      return `The bridge will accept files coded ${pw} and route them without changing the code.`;
    }
    if (canonValid && !isSelfRewrite) {
      return `The bridge will rewrite ${pw} to ${canon} on PixelWrangler files before upload.`;
    }
    return null;
  }, [mode, pw, canon, pwValid, canonValid, isSelfRewrite]);

  const handleAdd = () => {
    if (!canSubmit) return;
    onAdd({
      pwCode: pw,
      canonicalCode: mode === 'rewrite' ? canon : undefined,
      note: note.trim() || undefined,
    });
    // The parent closes on mutation success; keep the form intact until then.
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Add a colour code</DialogTitle>
          <DialogDescription>
            Tell the bridge how to handle a PixelWrangler colour code it does not already know.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <RadioGroup
            value={mode}
            onValueChange={(v) => setMode(v as Mode)}
            className="grid gap-2"
          >
            {MODE_OPTIONS.map((opt) => {
              const Icon = opt.icon;
              const active = mode === opt.id;
              const inputId = `colour-mode-${opt.id}`;
              return (
                <label
                  key={opt.id}
                  htmlFor={inputId}
                  className={cn(
                    'flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors hover:bg-accent',
                    active ? 'border-primary bg-accent' : 'border-border',
                  )}
                >
                  <RadioGroupItem value={opt.id} id={inputId} className="mt-0.5 shrink-0" />
                  <Icon className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
                  <div className="min-w-0">
                    <div className="font-medium">{opt.title}</div>
                    <div className="text-sm text-muted-foreground">{opt.desc}</div>
                  </div>
                </label>
              );
            })}
          </RadioGroup>

          <div className="flex items-end gap-2">
            <div className="flex-1 space-y-1">
              <Label htmlFor="cc-pw" className="text-sm text-muted-foreground">
                PixelWrangler code
              </Label>
              <Input
                id="cc-pw"
                value={pwCode}
                onChange={(e) => setPwCode(e.target.value)}
                placeholder="WHT"
                maxLength={10}
                className="h-11 font-mono uppercase placeholder:normal-case"
                autoComplete="off"
                spellCheck={false}
                autoFocus
              />
            </div>
            {mode === 'rewrite' ? (
              <>
                <ArrowRight className="mb-3 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                <div className="flex-1 space-y-1">
                  <Label htmlFor="cc-canon" className="text-sm text-muted-foreground">
                    Rewrite to
                  </Label>
                  <Input
                    id="cc-canon"
                    value={canonicalCode}
                    onChange={(e) => setCanonicalCode(e.target.value)}
                    placeholder="WH"
                    maxLength={10}
                    className="h-11 font-mono uppercase placeholder:normal-case"
                    autoComplete="off"
                    spellCheck={false}
                  />
                </div>
              </>
            ) : null}
          </div>

          <div className="space-y-1">
            <Label htmlFor="cc-note" className="text-sm text-muted-foreground">
              Note <span className="text-muted-foreground/70">(optional)</span>
            </Label>
            <Input
              id="cc-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. White tee, PixelWrangler emits WHT"
              maxLength={500}
              className="h-11"
            />
          </div>

          {/* Live preview / inline error — single aria-live region. */}
          <p role="status" aria-live="polite" className="min-h-[1.25rem] text-sm">
            {error ? (
              <span className="text-destructive">{error}</span>
            ) : (
              <span className="text-muted-foreground">{preview}</span>
            )}
          </p>
        </div>

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button type="button" onClick={handleAdd} disabled={!canSubmit}>
            {isPending ? 'Adding…' : 'Add code'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
